/**
 * Position Analyzer
 * Analyzes cursor position to determine context: in command, argument, environment, etc.
 */

import { PositionInfo } from './types'
import { logDebug } from '@utils/logger'

/**
 * Analyze cursor position in LaTeX document
 */
export function analyzePosition(text: string, position: number): PositionInfo {
  const info: PositionInfo = {
    isInCommand: false,
    isInArgument: false,
    isInEnvironment: false,
    isInBraces: false,
    braceDepth: 0,
    isAfterBackslash: false,
    textBeforeCursor: text.substring(0, position),
    textAfterCursor: text.substring(position)
  }
  
  const textBefore = info.textBeforeCursor
  
  // Check if cursor is after backslash
  if (textBefore.length > 0 && textBefore[textBefore.length - 1] === '\\') {
    info.isAfterBackslash = true
    info.isInCommand = true
    return info
  }
  
  // Scan backwards to find backslash
  let backslashPos = -1
  for (let i = textBefore.length - 1; i >= 0 && i >= textBefore.length - 50; i--) {
    if (textBefore[i] === '\\' && (i === 0 || textBefore[i - 1] !== '\\')) {
      backslashPos = i
      break
    }
    // Stop at whitespace or newline
    if (textBefore[i] === ' ' || textBefore[i] === '\n' || textBefore[i] === '\t') {
      break
    }
  }
  
  if (backslashPos >= 0) {
    // Extract command name
    const afterBackslash = textBefore.substring(backslashPos + 1)
    const commandMatch = afterBackslash.match(/^([a-zA-Z]+(?:\*)?)/)
    if (commandMatch) {
      info.isInCommand = true
      info.commandName = commandMatch[1]
    }
  }
  
  // Analyze brace depth and position
  const braceInfo = analyzeBraces(textBefore)
  info.isInBraces = braceInfo.isInBraces
  info.braceDepth = braceInfo.depth
  info.isInArgument = braceInfo.isInArgument
  info.argumentIndex = braceInfo.argumentIndex
  
  // Check if in environment braces (after \begin{ or \end{)
  const envInfo = analyzeEnvironmentPosition(textBefore)
  info.isInEnvironment = envInfo.isInEnvironment
  info.environmentName = envInfo.environmentName
  
  logDebug('[PositionAnalyzer] Analyzed position', {
    isInCommand: info.isInCommand,
    commandName: info.commandName,
    isInBraces: info.isInBraces,
    isInEnvironment: info.isInEnvironment
  })
  
  return info
}

/**
 * Analyze brace depth and argument position
 */
function analyzeBraces(text: string): {
  isInBraces: boolean
  depth: number
  isInArgument: boolean
  argumentIndex?: number
} {
  let depth = 0
  let maxDepth = 0
  let argumentIndex = 0
  let inVerbatim = false
  
  for (let i = 0; i < text.length; i++) {
    // Check for verbatim
    if (text.substring(i, i + 9) === '\\begin{verbatim}') {
      inVerbatim = true
      i += 8
      continue
    }
    if (inVerbatim && text.substring(i, i + 7) === '\\end{verbatim}') {
      inVerbatim = false
      i += 6
      continue
    }
    
    if (inVerbatim) continue
    
    if (text[i] === '{' && (i === 0 || text[i - 1] !== '\\')) {
      depth++
      maxDepth = Math.max(maxDepth, depth)
      if (depth === 1) {
        argumentIndex++
      }
    } else if (text[i] === '}' && (i === 0 || text[i - 1] !== '\\')) {
      depth--
    }
  }
  
  return {
    isInBraces: depth > 0,
    depth: maxDepth,
    isInArgument: depth > 0,
    argumentIndex: depth > 0 ? argumentIndex : undefined
  }
}

/**
 * Analyze if cursor is in environment name position (after \begin{ or \end{)
 */
function analyzeEnvironmentPosition(text: string): {
  isInEnvironment: boolean
  environmentName?: string
} {
  // Look for \begin{ or \end{ before cursor
  const beginMatch = text.match(/\\begin\{([^}]*)$/)
  const endMatch = text.match(/\\end\{([^}]*)$/)
  
  if (beginMatch) {
    const envName = beginMatch[1]
    return {
      isInEnvironment: true,
      environmentName: envName || undefined
    }
  }
  
  if (endMatch) {
    const envName = endMatch[1]
    return {
      isInEnvironment: true,
      environmentName: envName || undefined
    }
  }
  
  return { isInEnvironment: false }
}

